import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { ScheduledPostStatus } from '../types';
import { calendarStateOf } from './calendarFilterModel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Calendar; each part uses its own slice of it */
import styles from './PromoteCalendar.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface PostStateMarkProps {
  status: ScheduledPostStatus;
}

/**
 * The mark of a post state on a card (design b10): a hollow dashed ring for a draft, "POST" in
 * amber for a post that needs you, a red "!" for a failed post, a tick for a published post and a
 * spinner for a post that goes out now. A scheduled post has no mark. The state word is in the
 * accessible name of the card, so the mark is only for the eye.
 */
const PostStateMarkComponent: React.FC<PostStateMarkProps> = ({ status }) => {
  const intl = useIntl();
  if (status === 'publishing') {
    return <span className={clsx(styles.sm, styles.smPublishing)} aria-hidden="true" />;
  }
  switch (calendarStateOf(status)) {
    case 'draft':
      return <span className={clsx(styles.sm, styles.smDraft)} aria-hidden="true" />;
    case 'needsYou':
      return (
        <span className={clsx(styles.sm, styles.smManual)} aria-hidden="true">
          {intl.formatMessage({ id: 'app.sml.promote.calendar.mark.post', defaultMessage: 'Post' })}
        </span>
      );
    case 'failed':
      return (
        <span className={clsx(styles.sm, styles.smFailed)} aria-hidden="true">
          !
        </span>
      );
    case 'published':
      return <span className={clsx(styles.sm, styles.smPosted)} aria-hidden="true" />;
    case 'canceled':
      return <span className={clsx(styles.sm, styles.smCanceled)} aria-hidden="true" />;
    case 'scheduled':
    default:
      return null;
  }
};

export const PostStateMark = TypedMemo(PostStateMarkComponent);
